import { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import {
  Plus, PencilSimple, Key, ProhibitInset, CheckCircle, UserCircle, Link as LinkIcon,
  MagnifyingGlass, Trash,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useConfirm } from "../components/ConfirmDialog";

const ROLE_OPTIONS = [
  { v: "admin_empresa", label: "Admin de la organización", desc: "Acceso total dentro de la organización" },
  { v: "gerente", label: "Gerente", desc: "Vista ejecutiva y gestión general" },
  { v: "encargado_social", label: "Encargado Social", desc: "Comunidades, autoridades, compromisos" },
  { v: "encargado_ambiental", label: "Encargado Ambiental", desc: "RCA, monitoreo, cumplimiento" },
  { v: "encargado_proyecto", label: "Encargado de Proyecto", desc: "Proyectos y sus compromisos" },
  { v: "consulta", label: "Consulta", desc: "Solo lectura" },
  { v: "auditor", label: "Auditor", desc: "Solo lectura + descarga de reportes" },
];

const ROLE_LABEL = ROLE_OPTIONS.reduce((acc, r) => ({ ...acc, [r.v]: r.label }), {
  super_admin: "Super Admin",
});

const emptyForm = () => ({
  full_name: "", email: "", password: "", role: "consulta", staff_id: "",
});

export default function UsersManagement() {
  const { user: me } = useAuth();
  const confirm = useConfirm();
  const [users, setUsers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const [pwDlg, setPwDlg] = useState(null); // { id, email, full_name }
  const [pwValue, setPwValue] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: us }, { data: st }] = await Promise.all([
        api.get("/users"),
        api.get("/staff", { params: { active: true } }),
      ]);
      setUsers(us);
      setStaff(st);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar la lista");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return users;
    const s = q.trim().toLowerCase();
    return users.filter(u =>
      (u.full_name || "").toLowerCase().includes(s) ||
      (u.email || "").toLowerCase().includes(s) ||
      (u.role || "").toLowerCase().includes(s)
    );
  }, [users, q]);

  const linkedStaffIds = useMemo(
    () => new Set(users.filter(u => u.staff_id).map(u => u.staff_id)),
    [users]
  );

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setDlgOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      full_name: u.full_name || "",
      email: u.email || "",
      password: "",
      role: u.role || "consulta",
      staff_id: u.staff_id || "",
    });
    setDlgOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/users/${editing.id}`, {
          email: form.email,
          full_name: form.full_name,
          role: form.role,
          staff_id: form.staff_id || "",
        });
        toast.success("Usuario actualizado");
      } else {
        await api.post("/users", {
          email: form.email,
          password: form.password,
          full_name: form.full_name,
          role: form.role,
          staff_id: form.staff_id || null,
        });
        toast.success(`Usuario "${form.full_name}" creado`);
      }
      setDlgOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u) => {
    if (u.id === me?.id) {
      toast.error("No puedes desactivarte a ti mismo");
      return;
    }
    if (u.is_active) {
      const ok = await confirm({
        title: `Desactivar a ${u.full_name}`,
        description: "El usuario dejará de poder iniciar sesión. Puedes reactivarlo cuando lo necesites.",
        confirmText: "Desactivar",
        danger: true,
      });
      if (!ok) return;
      try {
        await api.put(`/users/${u.id}`, { is_active: false });
        toast.success("Usuario desactivado");
        load();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "No se pudo desactivar");
      }
    } else {
      try {
        await api.put(`/users/${u.id}`, { is_active: true });
        toast.success("Usuario reactivado");
        load();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "No se pudo reactivar");
      }
    }
  };

  const deleteUser = async (u) => {
    if (u.id === me?.id) {
      toast.error("No puedes eliminarte a ti mismo");
      return;
    }
    const ok = await confirm({
      title: `Eliminar permanentemente a ${u.full_name}`,
      description: `Esta acción no se puede deshacer. Se borrará el usuario y su cuenta de login. Las asignaciones históricas (compromisos, proyectos, reuniones, documentos) se conservarán pero quedarán sin responsable asignado. Email: ${u.email}`,
      confirmText: "Eliminar definitivamente",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`${u.full_name} eliminado`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (!pwValue || pwValue.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    try {
      await api.post(`/users/${pwDlg.id}/password`, { password: pwValue });
      toast.success("Contraseña actualizada");
      setPwDlg(null);
      setPwValue("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cambiar la contraseña");
    }
  };

  return (
    <div data-testid="users-management-page">
      <PageHeader
        title="Usuarios y accesos"
        description="Personas de tu organización con acceso a SIGSAOP. Asigna roles, vincula con miembros del equipo interno para asignaciones, y controla altas y bajas."
        actions={
          <Button data-testid="new-user-btn" onClick={openCreate} className="gap-2">
            <Plus size={16} /> Nuevo usuario
          </Button>
        }
      />

      <div className="px-6 py-6 space-y-4">
        {/* Search bar */}
        <div className="flex items-end gap-3 rounded-md border border-slate-200 bg-white p-4">
          <div className="flex-1 max-w-md">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Buscar</Label>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                className="pl-8"
                placeholder="Nombre, email o rol…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="users-search-input"
              />
            </div>
          </div>
          <div className="text-xs text-slate-500 pb-2">
            {filtered.length} de {users.length} usuarios
          </div>
        </div>

        {loading ? (
          <div className="py-12 text-center text-slate-400">Cargando…</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-md border border-dashed border-slate-300 bg-white px-6 py-16 text-center">
            <UserCircle size={32} className="mx-auto text-slate-300 mb-2" />
            <div className="text-sm text-slate-500">
              {users.length === 0
                ? "Aún no hay usuarios adicionales. Crea el primero para dar acceso a tu equipo."
                : "Sin resultados para esa búsqueda."}
            </div>
          </div>
        ) : (
          <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3">Usuario</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Rol</th>
                  <th className="text-left px-4 py-3">Vinculado a equipo</th>
                  <th className="text-left px-4 py-3">Estado</th>
                  <th className="text-right px-4 py-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => {
                  const isMe = u.id === me?.id;
                  return (
                    <tr
                      key={u.id}
                      data-testid={`user-row-${u.id}`}
                      className={`border-t border-slate-100 ${!u.is_active ? "bg-slate-50/60 text-slate-500" : ""}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-full bg-brand-orange-100 text-brand-orange-700 flex items-center justify-center text-xs font-bold">
                            {(u.full_name || "?").slice(0, 1).toUpperCase()}
                          </div>
                          <div className="font-medium text-slate-900">{u.full_name}</div>
                          {isMe && (
                            <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                              Tú
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{u.email}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                          u.role === "admin_empresa"
                            ? "bg-brand-orange-50 text-brand-orange-700"
                            : "bg-slate-100 text-slate-700"
                        }`}>
                          {ROLE_LABEL[u.role] || u.role}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {u.staff_full_name ? (
                          <span className="inline-flex items-center gap-1">
                            <LinkIcon size={11} />
                            {u.staff_full_name}
                            {u.staff_cargo && <span className="text-slate-400"> · {u.staff_cargo}</span>}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {u.is_active ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-brand-green-50 text-brand-green-700 px-2 py-0.5 text-xs">
                            <CheckCircle size={11} weight="fill" /> Activo
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-600 px-2 py-0.5 text-xs">
                            <ProhibitInset size={11} /> Inactivo
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEdit(u)}
                            data-testid={`edit-user-${u.id}`}
                            title="Editar"
                          >
                            <PencilSimple size={14} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => { setPwDlg(u); setPwValue(""); }}
                            data-testid={`reset-pw-${u.id}`}
                            title="Cambiar contraseña"
                          >
                            <Key size={14} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleActive(u)}
                            disabled={isMe}
                            data-testid={`toggle-user-${u.id}`}
                            title={u.is_active ? "Desactivar" : "Reactivar"}
                          >
                            {u.is_active
                              ? <ProhibitInset size={14} className="text-red-600" />
                              : <CheckCircle size={14} className="text-brand-green-600" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteUser(u)}
                            disabled={isMe}
                            data-testid={`delete-user-${u.id}`}
                            title="Eliminar permanentemente"
                          >
                            <Trash size={14} className="text-red-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="max-w-lg" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar usuario" : "Nuevo usuario"}</DialogTitle>
            <DialogDescription>
              {editing
                ? "Puedes cambiar el nombre, el rol y la vinculación con un miembro del equipo. Para cambiar la contraseña, usa el botón de la fila."
                : "Crea una cuenta con login para tu equipo. Opcionalmente, vincula el usuario con una persona del equipo interno para que también aparezca como responsable asignable."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label>Nombre completo *</Label>
              <Input
                data-testid="user-name-input"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                data-testid="user-email-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
              {editing && (
                <div className="text-[11px] text-slate-500 mt-1">
                  Cambiar el email también cambia el usuario para iniciar sesión.
                </div>
              )}
            </div>
            {!editing && (
              <div>
                <Label>Contraseña inicial *</Label>
                <Input
                  data-testid="user-password-input"
                  type="text"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Mín. 6 caracteres"
                  required
                  minLength={6}
                />
                <div className="text-[11px] text-slate-500 mt-1">
                  El usuario podrá cambiarla después. Compártela por un canal seguro.
                </div>
              </div>
            )}
            {editing && (
              <div className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600">
                La contraseña no se cambia aquí. Usa el botón <Key size={11} className="inline" /> de la fila.
              </div>
            )}
            <div>
              <Label>Rol *</Label>
              <select
                data-testid="user-role-input"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                required
              >
                {ROLE_OPTIONS.map(r => (
                  <option key={r.v} value={r.v}>{r.label}</option>
                ))}
              </select>
              <div className="text-[11px] text-slate-500 mt-1">
                {ROLE_OPTIONS.find(r => r.v === form.role)?.desc}
              </div>
            </div>
            <div>
              <Label>Vincular con miembro del equipo (opcional)</Label>
              <select
                data-testid="user-staff-input"
                value={form.staff_id}
                onChange={(e) => setForm({ ...form, staff_id: e.target.value })}
                className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
              >
                <option value="">— Sin vinculación —</option>
                {staff.map(s => {
                  const linkedToOther = linkedStaffIds.has(s.id) && s.id !== editing?.staff_id;
                  return (
                    <option key={s.id} value={s.id} disabled={linkedToOther}>
                      {s.full_name}
                      {s.cargo ? ` — ${s.cargo}` : ""}
                      {linkedToOther ? " (ya vinculado)" : ""}
                    </option>
                  );
                })}
              </select>
              <div className="text-[11px] text-slate-500 mt-1">
                Al vincular, esta persona aparecerá como asignable en compromisos, proyectos y reuniones,
                y a su vez tendrá login con el rol seleccionado.
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setDlgOpen(false)}>Cancelar</Button>
              <Button data-testid="save-user-btn" type="submit" disabled={saving}>
                {saving ? "Guardando…" : (editing ? "Guardar cambios" : "Crear usuario")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Password reset dialog */}
      <Dialog open={!!pwDlg} onOpenChange={(o) => { if (!o) { setPwDlg(null); setPwValue(""); } }}>
        <DialogContent className="max-w-sm" data-testid="password-dialog">
          <DialogHeader>
            <DialogTitle>Cambiar contraseña</DialogTitle>
            <DialogDescription>
              Definir una nueva contraseña para <strong>{pwDlg?.full_name}</strong> ({pwDlg?.email}).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitPassword} className="space-y-4">
            <div>
              <Label>Nueva contraseña</Label>
              <Input
                data-testid="new-password-input"
                type="text"
                value={pwValue}
                onChange={(e) => setPwValue(e.target.value)}
                placeholder="Mín. 6 caracteres"
                minLength={6}
                required
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setPwDlg(null)}>Cancelar</Button>
              <Button data-testid="save-password-btn" type="submit">Guardar contraseña</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
